import { IrrigationDeviceBase } from "./IrrigationDeviceBase.js";
import {
    DeviceTypeConfigurations,
    type FirstPartyDeviceType,
    ParsedDataTypeSpecifier,
    SensorConfig,
    SensorType,
} from "verdi-common";
import { RecentSensorDataPoint } from "verdi-common/schemas/commonSubSchemas/recentSensorDataPoint";
import { VertexInfo } from "verdi-common/schemas/commonSubSchemas/vertexInfo";
import { SensorSpecifier } from "verdi-common/schemas/device/subSchemas/sensorSpecifier";
const SensorConfigurations = SensorConfig.SensorConfigurations;
export class IrrigationDeviceSensor {
    /**
     * Coordinate of device
     * @type number
     */
    lat: number;
    /**
     * Coordinate of device
     * @type number
     */
    lng: number;
    /**
     * Coordinate of device (in meters)
     * @type number
     */
    depth: number | undefined;

    /**
     * The kind of sensor
     * @type {sensorType}
     */
    sensorType: SensorType;

    get dataType(): ParsedDataTypeSpecifier {
        return SensorConfigurations[this.sensorType]?.parsedDataType;
    }

    /**
     * [minimum value, maximum value].
     * values outside the range will trigger an sms alert.
     * @type {number[]}
     */
    acceptableValueRange?: number[];

    acceptableRangeByValveState?: number[][];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userMetadata: Record<string, any>;

    connectedZoneIDs: string[];
    /**
     * @deprecated Use lastReading instead
     */
    lastDataPoint: { value: number; date: Date } = undefined;
    /**
     * The last reading from the sensor
     */
    lastReading: RecentSensorDataPoint = undefined;
    readingPeriod?: number = undefined;
    uplinkPeriod?: number = undefined;
    readingsPerUplink?: number = undefined;
    useForVerification?: boolean = false;
    acceptableRampUpTimeMs?: number;
    acceptableRampDownTimeMs?: number;
    sensorParameters: Record<string, number | string>;
    constructor(json: SensorSpecifier | Record<string, never>) {
        if (Object.keys(json).length === 0) {
            this.connectedZoneIDs = [];
            this.acceptableRangeByValveState = [];
            this.sensorParameters = {};
            return;
        }

        // After the empty check, we know json is SensorSpecifier
        const sensorData = json as SensorSpecifier;

        this.lat = sensorData.position?.lat || undefined;
        this.lng = sensorData.position?.lng || undefined;
        this.depth = sensorData.position?.depth || undefined;
        this.sensorType = sensorData.sensorType;
        this.readingPeriod = sensorData.readingPeriod;
        this.useForVerification = sensorData.useForVerification;
        this.acceptableRampUpTimeMs = sensorData.acceptableRampUpTimeMs;
        this.acceptableRampDownTimeMs = sensorData.acceptableRampDownTimeMs;
        this.uplinkPeriod = sensorData.uplinkPeriod;
        this.readingsPerUplink = sensorData.readingsPerUplink;
        this.acceptableValueRange = sensorData.acceptableValueRange;
        this.userMetadata = sensorData.userMetadata;
        this.connectedZoneIDs = sensorData.connectedZones || [];
        this.acceptableRangeByValveState = sensorData.acceptableRangeByValveState || [];
        this.sensorParameters = sensorData.sensorParameters ?? {};
        this.lastReading = sensorData.lastReading || undefined;
    }
    getConnectedZoneIDs(device: IrrigationDeviceBase): string[] {
        let connectedIDsList = ["default"];
        if (this.connectedZoneIDs && Array.isArray(this.connectedZoneIDs) && this.connectedZoneIDs.length > 0) {
            connectedIDsList = [...this.connectedZoneIDs];
        }

        return Array.from(
            new Set(
                connectedIDsList.reduce((result, zid) => {
                    if (zid === "default") {
                        if (DeviceTypeConfigurations[device.type as FirstPartyDeviceType]?.valveType === "block") {
                            result.push(...device.connectedZoneIDs);
                            return result;
                        }
                        const sensorIndex = device.connectedSensors.indexOf(this);

                        result.push(
                            device.connectedZoneIDs[sensorIndex] ??
                                device.connectedZoneIDs[0] ??
                                device.connectedZoneIDs[1],
                        );
                        return result;
                    }
                    result.push(zid);
                    return result;
                }, []),
            ),
        );
    }
    toJSON(): Omit<SensorSpecifier, "sensorIndex"> | null {
        if (this.sensorType) {
            return new SensorSpecifier({
                position: VertexInfo.utilities.createFromLatLng(this.lat, this.lng, this.depth),
                sensorType: this.sensorType,
                acceptableValueRange: this.acceptableValueRange,
                sensorParameters: this.sensorParameters,
                userMetadata: this.userMetadata,
                connectedZones: this.connectedZoneIDs,
                acceptableRangeByValveState: this.acceptableRangeByValveState,
                useForVerification: this.useForVerification,
                acceptableRampUpTimeMs: this.acceptableRampUpTimeMs,
                acceptableRampDownTimeMs: this.acceptableRampDownTimeMs,
            } as SensorSpecifier);
        } else {
            return null;
        }
    }
}
